/** Mobile OS the current browser is running on. */
export type MobileOS = 'android' | 'ios' | 'other';

interface UserAgentDataLike {
  platform?: string;
}

/**
 * Best-effort detection of the visitor's mobile OS from the browser.
 *
 * Android uses the modern `navigator.userAgentData.platform` when available
 * (Chromium) and falls back to a User-Agent regex. iOS covers iPhone/iPod plus
 * iPadOS, which since iPadOS 13 reports itself as "MacIntel" but exposes touch
 * points — so we treat a touch-capable "Mac" as iOS.
 *
 * Returns `'other'` for desktop and when `navigator` is unavailable (e.g. a
 * future SSR / prerender pass), so callers safely render nothing there.
 */
export function getMobileOS(): MobileOS {
  if (typeof navigator === 'undefined') return 'other';

  const uaData = (
    navigator as Navigator & { userAgentData?: UserAgentDataLike }
  ).userAgentData;
  if (uaData?.platform === 'Android') return 'android';

  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';

  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ masquerades as desktop Safari on macOS. `navigator.platform`
    // is deprecated but still the most reliable signal here, so it's a fallback.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';

  return 'other';
}

/**
 * True for iPhone/iPod or Android+Mobile. False for iPad, desktop, and
 * when `navigator` is missing. Warns if the UA string is empty.
 */
export function getIsPhone(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  if (ua === '') {
    console.warn(
      'getIsPhone: navigator.userAgent is empty; treating as not a phone'
    );
    return false;
  }
  if (/iPhone|iPod/.test(ua)) return true;
  if (/android/i.test(ua) && /mobile/i.test(ua)) return true;
  return false;
}
